import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { HERO } from "@/constants/testIds/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBmaW5hbmNlJTIwdGVhbSUyMG1lZXRpbmd8ZW58MHx8fHwxNzgyOTQyNTQ2fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { t } = useLanguage();
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative pt-32 md:pt-40 pb-20 md:pb-32 overflow-hidden"
    >
      {/* Grain texture overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Eyebrow */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={0}
          className="font-sans uppercase tracking-[0.28em] text-xs text-[#8B1A1A] mb-8"
        >
          {t.hero.eyebrow}
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <h1 className="font-serif font-light text-[#1A1A1A] text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight">
              <motion.span variants={fadeUp} initial="hidden" animate="show" custom={1} className="block">
                {t.hero.title[0]}
              </motion.span>
              <motion.span variants={fadeUp} initial="hidden" animate="show" custom={2} className="block">
                <span className="italic text-[#8B1A1A]">{t.hero.title[1]}</span>{" "}
                {t.hero.title[2]}
              </motion.span>
            </h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-8 md:mt-10 max-w-xl text-[#5A5A5A] text-base md:text-lg leading-relaxed"
            >
              {t.hero.lede}
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#contact"
                data-testid={HERO.primaryCta}
                onClick={scrollTo("contact")}
                className="group inline-flex items-center gap-2 bg-[#8B1A1A] text-white text-xs uppercase tracking-[0.24em] px-7 py-4 hover:bg-[#1A1A1A] transition-colors duration-300"
              >
                {t.hero.primaryCta}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <a
                href="#services"
                data-testid={HERO.secondaryCta}
                onClick={scrollTo("services")}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#1A1A1A] px-7 py-4 border border-[#1A1A1A]/20 hover:border-[#8B1A1A] hover:text-[#8B1A1A] transition-colors duration-300"
              >
                {t.hero.secondaryCta}
              </a>
            </motion.div>
          </div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={5}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Consultoria contabilística profissional"
                className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700 hover:scale-105"
                loading="eager"
              />
              <div className="absolute inset-0 bg-[#8B1A1A]/10 mix-blend-multiply" />
            </div>
            {/* Editorial number tag */}
            <div className="absolute -top-4 -left-4 md:-left-8 bg-[#F7F6F2] px-4 py-2 border border-[#1A1A1A]/10">
              <span className="font-serif text-xl text-[#8B1A1A]">01</span>
              <span className="ml-3 text-xs uppercase tracking-[0.2em] text-[#5A5A5A]">
                {t.hero.eyebrow.split("—")[0].trim()}
              </span>
            </div>
          </motion.div>
        </div>

        {/* Stats strip */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={6}
          className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-3 border-t border-[#1A1A1A]/10"
        >
          {[
            [t.hero.stat1Value, t.hero.stat1Label],
            [t.hero.stat2Value, t.hero.stat2Label],
            [t.hero.stat3Value, t.hero.stat3Label],
          ].map(([value, label], i) => (
            <div
              key={i}
              className={`py-8 md:py-10 md:pr-10 ${
                i < 2 ? "md:border-r border-[#1A1A1A]/10" : ""
              } ${i > 0 ? "border-t md:border-t-0 border-[#1A1A1A]/10" : ""}`}
            >
              <div className="font-serif text-5xl md:text-6xl text-[#1A1A1A] leading-none">
                {value}
              </div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-[#5A5A5A]">
                {label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
